import { defineStore } from "pinia";
import {computed, watch, onMounted, ref} from "vue";
const isSSR = () => typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.SSR;

function getSystemPrefersDark() {
    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia("(prefers-color-scheme: dark)");
    }
    return { matches: false };
}

export const useColorStore = defineStore("colorStore", () => {
    // Default light theme colors
    const lightTheme =  {
        darkest: "",
            dark: "",
            primary: "",
            light: "",
            lightest: "",
            white: "",
            whitest: "",
            accent: "",
            accentDark: "",
            accentLight: "",
            textPrimary: "#333",
            textSecondary: "#555",
            textTertiary: "#777"
    }


    // Dark mode colors
    const darkTheme = {
        background: "#0a1929",
        text: "#e6eff6",
        primary: "#1d3b6d",
        accent: "#267d73",
        footerBg: "#112b45",
        navBg: "rgba(10, 25, 41, 0.8)",
        navText: "#e6eff6"
    };

    // Reactive color state
    const colors = ref({ ...lightTheme });
    const theme = ref("glass");

    // Detect system dark mode preference
    const systemPrefersDark = getSystemPrefersDark();

    // Check if dark mode is enabled
    const isDarkMode = computed(() => !isSSR() ? document.documentElement.classList.contains("dark") : false);

    // Update colors dynamically
    function setColors(newColors) {
        colors.value = { ...newColors }; // ✅ Ensure reactivity is preserved
    }

    function setTheme(newTheme) {
        theme.value = newTheme;
    }


    // Toggle Dark Mode
    function toggleDarkMode() {
        const newDarkMode = !isDarkMode.value;
        document.documentElement.classList.toggle("dark", newDarkMode);
        localStorage.setItem("draksy-theme", newDarkMode ? "dark" : "light");
        setColors(newDarkMode ? darkTheme : lightTheme);
    }

    // Initialize Theme & Apply System Preferences
    function initializeTheme() {
        if (import.meta.env.SSR)  return; // ✅ Prevent running in SSR

        const savedTheme = localStorage.getItem("draksy-theme");
        const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        let useDark = savedTheme === "dark" || (!savedTheme && prefersDark);

        document.documentElement.classList.toggle("dark", useDark);
        setColors(useDark ? darkTheme : lightTheme);
    }

    // Auto-update theme if system preference changes
    watch(() => systemPrefersDark.matches, (newValue) => {
        if (!localStorage.getItem("draksy-theme")) {
            document.documentElement.classList.toggle("dark", newValue);
            setColors(newValue ? darkTheme : lightTheme);
        }
    });

    // Initialize on mount
    onMounted(() => {
        initializeTheme();
    });

    return { colors, setColors, toggleDarkMode, initializeTheme, isDarkMode, theme, setTheme };
});
